import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Modal, Popover, Spin, Table, Tag } from "antd";
import { DeleteOutlined, ExclamationCircleTwoTone, PlusOutlined } from '@ant-design/icons';
import axios from "axios";
import HasPermissions from "../../../../../containers/HasPermissions";
import AddStoredPaymentMethodModal from './AddStoredPaymentMethodModal';
import UpdateStoredPaymentMethodExpiryModal from './UpdateStoredPaymentMethodExpiryModal';
import { axiosErrorHandler } from "../../../../../swzutils";
import PermissionDropdownButton from "../../../components/PermissionDropdownButton";
import { useSearchParams } from "react-router-dom";
import DashboardConstants from "../../dashboard/DashboardConstants";
export const ADD_PAYMENT_METHOD_QUERY_PARAM = "addPaymentMethod";
// e.g. "AMERICAN_EXPRESS" -> "American Express"
const formatBrand = (brand) => brand.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
const StoredPaymentMethodsTable = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        refresh() {
            loadPaymentMethods();
        },
    }));
    const [loading, setLoading] = useState(false);
    const [showAddStoredCreditCardModal, setShowAddStoredCreditCardModal] = useState(false);
    const [editExpiryPaymentMethod, setEditExpiryPaymentMethod] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        if (searchParams.get(ADD_PAYMENT_METHOD_QUERY_PARAM)) {
            setShowAddStoredCreditCardModal(true);
            setSearchParams(params => {
                params.delete(ADD_PAYMENT_METHOD_QUERY_PARAM);
                return params;
            });
        }
    });
    useEffect(() => {
        if (props.tenantId && props.tenantId) {
            loadPaymentMethods();
        }
    }, [props.tenantId]);
    const loadPaymentMethods = () => {
        let url = '/admin/v1/billing/payment-methods';
        if (props.tenantId) {
            url += `?tenantId=${props.tenantId}`;
        }
        setLoading(true);
        axios.get(url)
            .then(response => {
            setPaymentMethods(response.data);
        })
            .catch(axiosErrorHandler)
            .finally(() => {
            setLoading(false);
        });
    };
    const confirmRemove = (paymentMethod) => {
        Modal.confirm({
            title: 'Are you sure?',
            content: 'Remove stored card no **** ' + paymentMethod.last4 + " (expiry " + paymentMethod.expMonth + "/" + paymentMethod.expYear + ")",
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => {
                removeStoredPaymentMethod(paymentMethod);
            }
        });
    };
    const removeStoredPaymentMethod = (paymentMethod) => {
        setLoading(true);
        let url = `/admin/v1/billing/payment-methods/${paymentMethod.id}`;
        if (props.tenantId) {
            url += `?tenantId=${props.tenantId}`;
        }
        axios.delete(url)
            .then(( /*response*/) => {
            const filteredArray = paymentMethods.filter(r => r.id !== paymentMethod.id);
            setPaymentMethods([...filteredArray]);
        })
            .catch((error) => {
            axiosErrorHandler(error);
        })
            .finally(() => {
            setLoading(false);
        });
    };
    const setAsDefaultPaymentMethod = (paymentMethod) => {
        setLoading(true);
        let url = `/admin/v1/billing/payment-methods/${paymentMethod.id}`;
        if (props.tenantId) {
            url += `?tenantId=${props.tenantId}`;
        }
        axios.patch(url, {
            isDefault: true
        }).then(( /*response*/) => {
            loadPaymentMethods();
        }).catch((error) => {
            axiosErrorHandler(error);
        }).finally(() => {
            setLoading(false);
        });
    };
    const onCardAdded = () => {
        setShowAddStoredCreditCardModal(false);
        loadPaymentMethods();
    };
    const onCardUpdated = () => {
        setEditExpiryPaymentMethod(null);
        loadPaymentMethods();
    };
    const { tenantId } = props;
    if (!tenantId) {
        return (_jsx("p", { children: "Select a tenant" }));
    }
    const columns = [{
            title: 'Brand',
            dataIndex: 'brand',
            width: '20%',
            render: (text /*, record*/) => {
                return formatBrand(text);
            }
        }, {
            title: 'Expiry',
            dataIndex: 'expMonth',
            width: '20%',
            render: (text, record) => {
                const expiry = `${record.expMonth}/${record.expYear}`;
                return (_jsxs(_Fragment, { children: [expiry, record.expired ? _jsxs(Popover, { content: "Card has expired!", children: ["\u00A0", _jsx(ExclamationCircleTwoTone, { twoToneColor: DashboardConstants.WARNING_ICON_COLOR })] }) : null] }));
            }
        }, {
            title: 'Card No',
            dataIndex: 'last4',
            width: '20%',
            render: (text /*, record*/) => {
                return "****" + text;
            }
        }, {
            title: 'Default',
            dataIndex: 'isDefault',
            width: '20%',
            render: (text /*, record*/) => {
                return text ? _jsx(Tag, { children: "Default" }) : "";
            }
        }, {
            title: 'Action',
            key: 'action',
            width: '25%',
            render: (text, record) => {
                return (_jsx(PermissionDropdownButton, { label: (_jsxs(_Fragment, { children: [_jsx(DeleteOutlined, {}), "\u00A0Remove"] })), onClick: () => confirmRemove(record), menuItems: [{
                            key: "set-as-default",
                            label: 'Set as Default',
                            condition: !record.isDefault && !record.expired,
                            onClick: () => setAsDefaultPaymentMethod(record)
                        }, {
                            key: "edit-expiry",
                            condition: record.canEditExpiry,
                            label: 'Edit Expiry',
                            onClick: () => setEditExpiryPaymentMethod(record)
                        }] }));
            }
        }];
    return (_jsxs("div", { children: [_jsx("div", { className: "table-operations clearfix", children: _jsx(HasPermissions, { permissions: ['admin:billing:paymentMethods:create'], children: _jsx(Button, { onClick: () => setShowAddStoredCreditCardModal(true), title: "Add a Stored Card", icon: _jsx(PlusOutlined, {}), children: "Add a new Stored Card" }) }) }), _jsx(Spin, { spinning: loading, children: _jsx(Table, { size: "middle", rowKey: record => record.id, columns: columns, scroll: { x: 450 }, dataSource: paymentMethods, pagination: false }) }), _jsx(AddStoredPaymentMethodModal, { open: showAddStoredCreditCardModal, tenantId: tenantId, onCancel: () => setShowAddStoredCreditCardModal(false), onAdded: onCardAdded }), _jsx(UpdateStoredPaymentMethodExpiryModal, { open: !!editExpiryPaymentMethod, tenantId: tenantId, paymentMethod: editExpiryPaymentMethod, onCancel: () => setEditExpiryPaymentMethod(null), onUpdated: onCardUpdated })] }));
});
export default StoredPaymentMethodsTable;
